import { Button, buttonVariants } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/ui/use-toast";
import { Input } from "@/components/ui/input";
import usePostData from "@/hooks/usePostData";
import { cn } from "@/libs/utils";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { z } from "zod";
import PasswordStrengthCheck from "./PasswordStrengthCheck";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Check, Eye, EyeOff, UserPlus, Loader } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";

type Subcontractor = {
  id: number;
  nom?: string;
  prenom?: string;
  mail?: string;
  access: boolean;
  esgWriteAccess?: boolean;
  isCoworker?: boolean;
  organisationId?: number | null;
  organisationName?: string;
};

type OrganisationOption = {
  id: number;
  name: string;
  siret?: string | null;
};

type AddSubcontractorDialogProps = {
  open: boolean;
  onHandleOpen: (open: boolean) => void;
  onSaveSuccess: (data: any, optimisticMeta?: any) => void;
  onOptimisticSave?: (data: any) => any;
  onSaveError?: (optimisticMeta?: any) => void;
  hasTrigger?: boolean;
  subcontractorToEdit: Subcontractor | null;
  isEditMode: boolean;
  isCoworker?: boolean;
  organisationSuggestions?: OrganisationOption[];
};

const AddSubcontractorDialog = ({
  open,
  onHandleOpen,
  onSaveSuccess,
  onOptimisticSave,
  onSaveError,
  hasTrigger = true,
  subcontractorToEdit,
  isEditMode = false,
  isCoworker = false,
  organisationSuggestions = [],
}: AddSubcontractorDialogProps) => {
  const [loadingSave, setLoadingSave] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
  const [title, setTitle] = useState("");
  const [organisationOptions, setOrganisationOptions] = useState<OrganisationOption[]>([]);
  const [selectedOrganisationId, setSelectedOrganisationId] = useState<number | null>(null);
  const [organisationDropdownOpen, setOrganisationDropdownOpen] = useState(false);
  const { postData } = usePostData();
  const hidePostActions = useHidePostActions();
  const [isPasswordOk, setIsPasswordOk] = useState(true);

  const { t } = useTranslation("dashboard", {
    keyPrefix: "soustraitant",
  });

  function getFormSchema(isEditMode: boolean, isPasswordOk: boolean, isCoworker: boolean) {
    const baseFields = {
      lastname: z.string().min(2, {
        message: t("Le nom de famille doit contenir au moins 2 caractères."),
      }),
      firstname: z.string().min(2, {
        message: t("Le prénom doit contenir au moins 2 caractères."),
      }),
      email: z.string().min(2, {
        message: t("L'adresse e-mail n'est pas valide."),
      }),
      organisationName: isCoworker
        ? z.string().optional()
        : z.string().min(2, {
          message: "Le nom de l'organisation doit contenir au moins 2 caractères.",
        }),
      access: z.enum(["restricted", "unrestricted"], {
        required_error: t("Veuillez sélectionner un type d'accès."),
      }),
      esgWriteAccess: z.boolean().optional(),
    };

    const passwordField = z
      .string()
      .min(8, t("Le mot de passe doit comporter au moins 8 caractères."))
      .refine((val) => /[a-z]/.test(val), {
        message: t(
          "Le mot de passe doit contenir au moins une lettre minuscule."
        ),
      })
      .refine((val) => /[A-Z]/.test(val), {
        message: t(
          "Le mot de passe doit contenir au moins une lettre majuscule."
        ),
      })
      .refine((val) => /\d/.test(val), {
        message: t("Le mot de passe doit contenir au moins un chiffre."),
      })
      .refine((val) => /[!@#$%^&*(),.?":{}|<>]/.test(val), {
        message: t(
          "Le mot de passe doit contenir au moins un caractère spécial."
        ),
      });

    const confirmPasswordField = z
      .string()
      .min(8, {
        message: t(
          "La confirmation du mot de passe doit contenir au moins 8 caractères."
        ),
      })
      .regex(passwordRegex, {
        message: t(
          "La confirmation du mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre."
        ),
      });

    const shouldValidatePassword = !isEditMode || (isEditMode && !isPasswordOk);

    const schema = z.object({
      ...baseFields,
      ...(shouldValidatePassword
        ? {
          password: passwordField,
          confirmPassword: confirmPasswordField,
        }
        : {
          password: z.string().optional(),
          confirmPassword: z.string().optional(),
        }),
    });

    // Ajoute la validation que les deux mdp doivent correspondre
    return shouldValidatePassword
      ? schema.refine((data) => data.password === data.confirmPassword, {
        path: ["confirmPassword"],
        message: t("Les mots de passe ne correspondent pas."),
      })
      : schema;
  }

  const formSchema = getFormSchema(isEditMode, isPasswordOk, isCoworker);
  type SubcontractorFormValues = z.infer<typeof formSchema>;
  const form = useForm<SubcontractorFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstname: "",
      lastname: "",
      email: "",
      organisationName: "",
      password: "",
      confirmPassword: "",
      access: "unrestricted",
      esgWriteAccess: false,
    },
  });
  const [pendingExistingAccount, setPendingExistingAccount] = useState<{
    values: SubcontractorFormValues;
    message: string;
  } | null>(null);

  useEffect(() => {
    if (isEditMode === false) {
      setTitle(t("Ajouter un intervenant"));
      form.reset({
        firstname: "",
        lastname: "",
        email: "",
        organisationName: "",
        password: "",
        confirmPassword: "",
        access: "unrestricted",
        esgWriteAccess: false,
      });
      setSelectedOrganisationId(null);
      setOrganisationOptions([]);
    } else if (isEditMode === true && subcontractorToEdit) {
      setTitle(t("Modifier un intervenant"));
      form.reset({
        firstname: subcontractorToEdit.prenom || "",
        lastname: subcontractorToEdit.nom || "",
        email: subcontractorToEdit.mail || "",
        organisationName: subcontractorToEdit.organisationName || "",
        password: "",
        confirmPassword: "",
        access: subcontractorToEdit.access === true ? "unrestricted" : "restricted",
        esgWriteAccess: subcontractorToEdit.esgWriteAccess === true,
      });
      setSelectedOrganisationId(subcontractorToEdit.organisationId ?? null);
      setOrganisationOptions(
        subcontractorToEdit.organisationId && subcontractorToEdit.organisationName
          ? [
            {
              id: subcontractorToEdit.organisationId,
              name: subcontractorToEdit.organisationName,
              siret: null,
            },
          ]
          : []
      );
    }
  }, [isEditMode, subcontractorToEdit]);

  const password = form.watch("password");
  const organisationNameValue = form.watch("organisationName");

  useEffect(() => {
    if (isCoworker) {
      return;
    }

    const normalizedValue = String(organisationNameValue || "").trim();
    const selectedOption = organisationOptions.find(
      (option) => option.id === selectedOrganisationId
    );

    if (selectedOption && selectedOption.name !== normalizedValue) {
      setSelectedOrganisationId(null);
    }

    if (normalizedValue.length < 2) {
      setOrganisationOptions(selectedOption ? [selectedOption] : []);
      return;
    }
  }, [organisationNameValue, isCoworker, selectedOrganisationId]);

  const filteredOrganisationOptions = useMemo(() => {
    const normalizedValue = String(organisationNameValue || "").trim().toLowerCase();
    if (normalizedValue.length < 2) {
      return organisationOptions;
    }

    return organisationSuggestions.filter((option) =>
      option.name.toLowerCase().includes(normalizedValue)
    );
  }, [organisationNameValue, organisationOptions, organisationSuggestions]);

  type ExistingAccountCheckResult =
    | false
    | { needsConfirmation: false }
    | { needsConfirmation: true; message: string };

  const checkExistingAccount = async (
    email: string,
  ): Promise<ExistingAccountCheckResult> => {
    const token =
      localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_ENDPOINT}/check-existing-user`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            email,
            targetRole: isCoworker ? "ROLE_COWORKER" : "ROLE_SUBCONTRACTOR",
            excludeId: isEditMode ? subcontractorToEdit?.id : undefined,
          }),
        },
      );

      const result = await response.json().catch(() => null);
      if (!response.ok) {
        toast({
          className: "text-red-500",
          title: result?.message || "Cette adresse email est déjà utilisée.",
        });
        return false;
      }

      if (!result?.existingUser) {
        return {
          needsConfirmation: false,
        };
      }

      return {
        needsConfirmation: true,
        message: isCoworker
          ? "Cette adresse email existe déjà comme Porteur de projet  ou autre rôle. Voulez-vous aussi rattacher ce compte comme collaborateur ? Les données nom, prénom,mot de passe et organisation ne seront pas modifiées."
          : "Cette adresse email existe déjà comme Porteur de projet  ou autre rôle. Voulez-vous aussi rattacher ce compte comme intervenant ? Les données nom, prénom, mot de passe et organisation ne seront pas modifiées.",
      };
    } catch {
      return {
        needsConfirmation: false,
      };
    }
  };

  const buildOptimisticSubcontractor = (data: SubcontractorFormValues) => {
    const userType = isCoworker ? "coworkers" : "subcontractors";

    return {
      id: isEditMode
        ? subcontractorToEdit?.id
        : `optimistic-${userType}-${Date.now()}`,
      firstname: data.firstname,
      lastname: data.lastname,
      email: data.email,
      status: subcontractorToEdit?.status ?? 1,
      fullAccess: data.access === "unrestricted",
      access: data.access,
      esgWriteAccess:
        data.access === "restricted" && data.esgWriteAccess === true,
      organisationId: selectedOrganisationId,
      organisationName: data.organisationName ?? "",
    };
  };

  const submitSubcontractor = async (
    data: SubcontractorFormValues,
    confirmExistingUser = false,
    optimisticMeta?: any,
  ) => {
    const userType = isCoworker ? "coworkers" : "subcontractors";
    const activeOptimisticMeta =
      optimisticMeta ?? onOptimisticSave?.(buildOptimisticSubcontractor(data));
    if (!optimisticMeta) {
      onHandleOpen(false);
    }
    setPendingExistingAccount(null);

    const urlApi = isEditMode
      ? `${import.meta.env.VITE_IP_API}/api/users/${userType}/edit/${subcontractorToEdit?.id
      }`
      : `${import.meta.env.VITE_IP_API}/api/users/${userType}/create`;

    const res = await postData({
      url: urlApi,
      data: {
        firstname: data.firstname,
        lastname: data.lastname,
        email: data.email,
        organisationId: selectedOrganisationId,
        organisationName: data.organisationName ?? "",
        mdp: data.password ?? "",
        access: data.access,
        esgWriteAccess:
          data.access === "restricted" && data.esgWriteAccess === true,
        confirmExistingUser,
      },
      skipCacheRefresh: true,
    });

    if (res) {
      toast({
        title: res?.linkedExistingCarrier
          ? "Le porteur existant a bien été rattaché comme intervenant."
          : "L'intervenant a bien été ajouté.",
        duration: 1000,
      });
      onSaveSuccess(res, activeOptimisticMeta);
      setLoadingSave(false);
      return true;
    }
    onSaveError?.(activeOptimisticMeta);
    toast({
      title: t(
        "Une erreur est survenue, l'intervenant n'a pas pu être ajouté."
      ),
      duration: 1000,
    });
    setLoadingSave(false);
    return false;
  };

  const handleSave = async (data: SubcontractorFormValues) => {
    setLoadingSave(true);
    const optimisticMeta = onOptimisticSave?.(buildOptimisticSubcontractor(data));
    onHandleOpen(false);

    const existingAccount = await checkExistingAccount(data.email);
    if (!existingAccount) {
      onSaveError?.(optimisticMeta);
      setLoadingSave(false);
      return;
    }

    if (existingAccount.needsConfirmation) {
      onSaveError?.(optimisticMeta);
      onHandleOpen(true);
      setPendingExistingAccount({
        values: data,
        message: existingAccount.message,
      });
      setLoadingSave(false);
      return;
    }

    await submitSubcontractor(data, false, optimisticMeta);
  };

  const handleConfirmExistingAccount = async () => {
    if (!pendingExistingAccount) {
      return;
    }

    setLoadingSave(true);
    const isSaved = await submitSubcontractor(pendingExistingAccount.values, true);
    if (isSaved) {
      setPendingExistingAccount(null);
    }
  };

  useEffect(() => {
    // Vérifie si toutes les règles sont respectées
    const isValid =
      password?.length >= 8 &&
      /[a-z]/.test(password) &&
      /[A-Z]/.test(password) &&
      /\d/.test(password) &&
      /[!@#$%^&*(),.?":{}|<>]/.test(password);

    setIsPasswordValid(isValid);
  }, [password]);

  const selectedAccess = form.watch("access");

  if (hidePostActions) return null;
  return (
    <>
      <Dialog open={open} onOpenChange={onHandleOpen}>
        {hasTrigger && (
          <DialogTrigger asChild>
            <Button className={buttonVariants({ variant: "default" })}>
              <UserPlus className="w-4 h-4 mr-2" />
              {isCoworker ? "Ajouter un collaborateur" : t("Ajouter un intervenant")}
            </Button>
          </DialogTrigger>
        )}
        <DialogContent
          className={cn(
            "sm:max-h-[80vh] sm:max-w-[50vw] overflow-y-auto",
            pendingExistingAccount && "pointer-events-none opacity-40"
          )}
        >
          <DialogHeader className="">
            <DialogTitle asChild>
              <h2 className="mb-2 mt-5 flex gap-4 text-center text-xl font-bold">
                {isCoworker ? "Ajouter un collaborateur" : title}{" "}
              </h2>
            </DialogTitle>
            <DialogDescription asChild className="text-[#595959]">
              <p className="">
                {isCoworker ? "Chaque collaborateur peut accéder aux programmes ainsi qu’aux projets associés. Vous pouvez définir précisément les autorisations qui leur sont accordées, qu’il s’agisse d’un accès en lecture seule ou avec droits de modification." : t(
                  "Chaque intervenant peut accéder aux programmes ainsi qu’aux projets associés"
                )}
              </p>
            </DialogDescription>
          </DialogHeader>

          <div className="mt-5 h-full overflow-y-auto">
            <div className="pr-4">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(handleSave)}
                  className="space-y-8"
                >
                  <div className="flex flex-wrap gap-4">
                    <div className="flex flex-wrap gap-4">
                      {/* Email en pleine largeur */}
                      <div className="w-full flex  gap-4">
                        <div className="w-1/2">
                          <FormField
                            control={form.control}
                            name="access"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Droit</FormLabel>
                                <FormControl>
                                  <Select
                                    value={field.value}
                                    onValueChange={(value) => {
                                      field.onChange(value);
                                    }}
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder={isCoworker ? "Choisir un type de permission" : "Choisir un type d'accès"} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectGroup>
                                        <SelectItem value="restricted">{isCoworker ? "Lecture seule" : t("Accès restreint")}</SelectItem>
                                        <SelectItem value="unrestricted">{isCoworker ? "Lecture / Écriture" : t("Accès complet")}</SelectItem>
                                      </SelectGroup>
                                    </SelectContent>
                                  </Select>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <div className="w-1/2">
                          <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t("Email")} *</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={t("Email")}
                                    {...field}
                                    type="email"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                      {!isCoworker && selectedAccess === "restricted" && (
                        <div className="w-full rounded-xl border border-emerald-200 bg-emerald-50/70 p-4">
                          <FormField
                            control={form.control}
                            name="esgWriteAccess"
                            render={({ field }) => (
                              <FormItem className="space-y-2">
                                <div className="flex items-start gap-3">
                                  <Checkbox
                                    checked={field.value === true}
                                    onCheckedChange={(value) =>
                                      field.onChange(value === true)
                                    }
                                  />
                                  <div className="space-y-1">
                                    <FormLabel className="cursor-pointer">
                                      Autoriser l'écriture de la partie ESG
                                    </FormLabel>
                                    <p className="text-sm text-slate-600">
                                      Cet intervenant restera en accès restreint sur le reste du projet, mais pourra modifier la partie ESG comme avec un accès complet.
                                    </p>
                                  </div>
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      )}
                      {/* Nom + Prénom */}
                      <div className="flex w-full gap-4">
                        <div className="w-1/2">
                          <FormField
                            control={form.control}
                            name="lastname"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t("Nom")} *</FormLabel>
                                <FormControl>
                                  <Input placeholder={t("Nom")} {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <div className="w-1/2">
                          <FormField
                            control={form.control}
                            name="firstname"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t("Prénom")} *</FormLabel>
                                <FormControl>
                                  <Input placeholder={t("Prénom")} {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      {!isCoworker && (
                        <div className="w-full">
                          <FormField
                            control={form.control}
                            name="organisationName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Organisation *</FormLabel>
                                <FormControl>
                                  <div className="relative">
                                    <Input
                                      placeholder="Organisation"
                                      {...field}
                                      autoComplete="off"
                                      onFocus={() => setOrganisationDropdownOpen(true)}
                                      onBlur={() => {
                                        window.setTimeout(() => {
                                          setOrganisationDropdownOpen(false);
                                        }, 150);
                                      }}
                                      onChange={(event) => {
                                        field.onChange(event);
                                        setOrganisationDropdownOpen(true);
                                      }}
                                    />
                                    {organisationDropdownOpen && String(field.value || "").trim().length >= 2 && (
                                      <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-white shadow-lg">
                                        {filteredOrganisationOptions.length > 0 ? (
                                          filteredOrganisationOptions.map((option) => {
                                            const isSelected = option.id === selectedOrganisationId;

                                            return (
                                              <button
                                                key={option.id}
                                                type="button"
                                                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                                                onMouseDown={(event) => {
                                                  event.preventDefault();
                                                  form.setValue("organisationName", option.name, {
                                                    shouldDirty: true,
                                                    shouldValidate: true,
                                                  });
                                                  setSelectedOrganisationId(option.id);
                                                  setOrganisationDropdownOpen(false);
                                                  setOrganisationOptions([option]);
                                                }}
                                              >
                                                <span className="truncate">
                                                  {option.name}
                                                  {option.siret ? ` • ${option.siret}` : ""}
                                                </span>
                                                {isSelected ? (
                                                  <Check className="ml-2 h-4 w-4 text-green-600" />
                                                ) : null}
                                              </button>
                                            );
                                          })
                                        ) : (
                                          <div className="px-3 py-2 text-sm text-muted-foreground">
                                            Aucune organisation existante. Cette saisie créera une nouvelle organisation.
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      )}

                      {/* Mot de passe + Confirmation mot de passe */}
                      {isEditMode && (
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Checkbox
                            checked={isPasswordOk}
                            onCheckedChange={(value) => {
                              setIsPasswordOk(!!value);
                            }}
                            aria-label="Select row"
                          />
                          <span>{t("Garder le mot de passe inchangé")}</span>
                        </label>
                      )}

                      {(isEditMode == false ||
                        (isEditMode && !isPasswordOk)) && (
                          <div className="flex w-full gap-4">
                            <div className="w-1/2">
                              <FormField
                                control={form.control}
                                name="password"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t("Mot de passe")} *</FormLabel>
                                    <FormControl>
                                      <div className="relative">
                                        <Input
                                          placeholder={t("Mot de passe")}
                                          {...field}
                                          type={
                                            showPassword ? "text" : "password"
                                          }
                                        />
                                        <button
                                          onClick={(e) => {
                                            e.preventDefault();
                                            setShowPassword(!showPassword);
                                          }}
                                          className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center"
                                        >
                                          {!showPassword ? <EyeOff /> : <Eye />}
                                        </button>
                                      </div>
                                    </FormControl>
                                    <FormMessage />
                                    {password && (
                                      <PasswordStrengthCheck
                                        password={password}
                                        isPasswordValid={isPasswordValid}
                                        className="mt-0"
                                      />
                                    )}
                                  </FormItem>
                                )}
                              />
                            </div>

                            <div className="w-1/2">
                              <FormField
                                control={form.control}
                                name="confirmPassword"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      {t("Confirmation mot de passe")} *
                                    </FormLabel>
                                    <FormControl>
                                      <div className="relative">
                                        <Input
                                          placeholder={t(
                                            "Confirmation mot de passe"
                                          )}
                                          {...field}
                                          type={
                                            showPasswordConfirm
                                              ? "text"
                                              : "password"
                                          }
                                        />
                                        <button
                                          onClick={(e) => {
                                            e.preventDefault();
                                            setShowPasswordConfirm(
                                              !showPasswordConfirm
                                            );
                                          }}
                                          className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center"
                                        >
                                          {!showPasswordConfirm ? (
                                            <EyeOff />
                                          ) : (
                                            <Eye />
                                          )}
                                        </button>
                                      </div>
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </form>
              </Form>
            </div>

            <div className="w-full flex justify-end mt-5">
              <DialogFooter className="pb-5">
                <Button
                  onClick={form.handleSubmit(handleSave)}
                  type="button"
                  disabled={loadingSave}
                >
                  {/* {loadingSave ? t("... sauvegarde en cours") : t("Sauvegarder")} */}
                  {loadingSave ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      {t("... sauvegarde en cours")}
                    </>
                  ) : (
                    <>{t("Sauvegarder")}</>
                  )}
                </Button>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!pendingExistingAccount}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !loadingSave) {
            setPendingExistingAccount(null);
          }
        }}
      >
        <DialogContent
          className="z-[10001] sm:max-w-md"
          overlayClassName="z-[10000] bg-black/60"
        >
          <DialogHeader>
            <DialogTitle>Compte existant</DialogTitle>
            <DialogDescription>
              {pendingExistingAccount?.message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={loadingSave}
              onClick={() => setPendingExistingAccount(null)}
            >
              Annuler
            </Button>
            <Button
              type="button"
              disabled={loadingSave}
              onClick={handleConfirmExistingAccount}
            >
              {loadingSave ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Confirmation...
                </>
              ) : (
                "Confirmer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
export default AddSubcontractorDialog;
